export function DateTimeFormatHelp() {
  return (
    <div>
      <blockquote>
        Date and time formats are specified by date and time pattern strings. Within date and time
        pattern strings, unquoted letters from 'A' to 'Z' and from 'a' to 'z' are interpreted as
        pattern letters representing the components of a date or time string. Text can be quoted
        using single quotes (') to avoid interpretation. "''" represents a single quote. All other
        characters are not interpreted; they're simply copied into the output string during
        formatting or matched against the input string during parsing.
        <br /><br />
        The following pattern letters are defined (all other characters from 'A' to 'Z' and from
        'a' to 'z' are reserved):<br />

        <table className="table table-condensed table-bordered" summary="Chart shows pattern letters, date/time component, presentation, and examples.">
          <thead>
            <tr>
              <th>Letter</th>
              <th>Date or Time Component</th>
              <th>Presentation</th>
              <th>Examples</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><code>G</code></td><td>Era designator</td><td>Text</td><td><code>AD</code></td></tr>
            <tr><td><code>y</code></td><td>Year</td><td>Year</td><td><code>1996</code>; <code>96</code></td></tr>
            <tr><td><code>Y</code></td><td>Week year</td><td>Year</td><td><code>2009</code>; <code>09</code></td></tr>
            <tr><td><code>M</code></td><td>Month in year</td><td>Month</td><td><code>July</code>; <code>Jul</code>; <code>07</code></td></tr>
            <tr><td><code>w</code></td><td>Week in year</td><td>Number</td><td><code>27</code></td></tr>
            <tr><td><code>W</code></td><td>Week in month</td><td>Number</td><td><code>2</code></td></tr>
            <tr><td><code>D</code></td><td>Day in year</td><td>Number</td><td><code>189</code></td></tr>
            <tr><td><code>d</code></td><td>Day in month</td><td>Number</td><td><code>10</code></td></tr>
            <tr><td><code>F</code></td><td>Day of week in month</td><td>Number</td><td><code>2</code></td></tr>
            <tr><td><code>E</code></td><td>Day name in week</td><td>Text</td><td><code>Tuesday</code>; <code>Tue</code></td></tr>
            <tr><td><code>u</code></td><td>Day number of week (1 = Monday, ..., 7 = Sunday)</td><td>Number</td><td><code>1</code></td></tr>
            <tr><td><code>a</code></td><td>Am/pm marker</td><td>Text</td><td><code>PM</code></td></tr>
            <tr><td><code>H</code></td><td>Hour in day (0-23)</td><td>Number</td><td><code>0</code></td></tr>
            <tr><td><code>k</code></td><td>Hour in day (1-24)</td><td>Number</td><td><code>24</code></td></tr>
            <tr><td><code>K</code></td><td>Hour in am/pm (0-11)</td><td>Number</td><td><code>0</code></td></tr>
            <tr><td><code>h</code></td><td>Hour in am/pm (1-12)</td><td>Number</td><td><code>12</code></td></tr>
            <tr><td><code>m</code></td><td>Minute in hour</td><td>Number</td><td><code>30</code></td></tr>
            <tr><td><code>s</code></td><td>Second in minute</td><td>Number</td><td><code>55</code></td></tr>
            <tr><td><code>S</code></td><td>Millisecond</td><td>Number</td><td><code>978</code></td></tr>
            <tr><td><code>z</code></td><td>Time zone</td><td>General time zone</td><td><code>Pacific Standard Time</code>; <code>PST</code>; <code>GMT-08:00</code></td></tr>
            <tr><td><code>Z</code></td><td>Time zone</td><td>RFC 822 time zone</td><td><code>-0800</code></td></tr>
            <tr><td><code>X</code></td><td>Time zone</td><td>ISO 8601 time zone</td><td><code>-08</code>; <code>-0800</code>; <code>-08:00</code></td></tr>
          </tbody>
        </table>

        Pattern letters are usually repeated, as their number determines the exact presentation:<br />

        <ul>
          <li>
            <strong>Text:</strong> For formatting, if the number of pattern letters is 4 or more,
            the full form is used; otherwise a short or abbreviated form is used if available.
            For parsing, both forms are accepted, independent of the number of pattern letters.
            <br /><br />
          </li>
          <li>
            <strong>Number:</strong> For formatting, the number of pattern letters is the minimum
            number of digits, and shorter numbers are zero-padded to this amount. For parsing, the
            number of pattern letters is ignored unless it's needed to separate two adjacent fields.
            <br /><br />
          </li>
          <li>
            <strong>Year:</strong> If the formatter's <code>Calendar</code> is the Gregorian
            calendar, the following rules are applied.
            <ul>
              <li>For formatting, if the number of pattern letters is 2, the year is truncated to
                2 digits; otherwise it is interpreted as a number.</li>
              <li>For parsing, if the number of pattern letters is more than 2, the year is
                interpreted literally, regardless of the number of digits. So using the pattern
                "MM/dd/yyyy", "01/11/12" parses to Jan 11, 12 A.D.</li>
              <li>During parsing, only strings consisting of exactly two digits will be parsed into
                the default century. Any other numeric string is interpreted literally. So
                "01/02/3" or "01/02/003" are parsed as Jan 2, 3 AD. Likewise, "01/02/-3" is
                parsed as Jan 2, 4 BC.</li>
            </ul>
            <br />
          </li>
          <li>
            <strong>Month:</strong> If the number of pattern letters is 3 or more, the month is
            interpreted as text; otherwise it is interpreted as a number.
            <br /><br />
          </li>
          <li>
            <strong>General time zone:</strong> Time zones are interpreted as text if they have
            names. For time zones representing a GMT offset value, the following syntax is used:
            <pre>{`GMTOffsetTimeZone: GMT Sign Hours : Minutes
Sign: one of + -
Hours: Digit | Digit Digit
Minutes: Digit Digit
Digit: one of 0 1 2 3 4 5 6 7 8 9`}</pre>
            Hours must be between 0 and 23, and Minutes must be between 00 and 59. For parsing,
            RFC 822 time zones are also accepted.
            <br /><br />
          </li>
          <li>
            <strong>RFC 822 time zone:</strong> For formatting, the RFC 822 4-digit time zone
            format is used:
            <pre>{`RFC822TimeZone: Sign TwoDigitHours Minutes
TwoDigitHours: Digit Digit`}</pre>
            TwoDigitHours must be between 00 and 23. For parsing, general time zones are also
            accepted.
          </li>
          <li>
            <strong>ISO 8601 Time zone:</strong> The number of pattern letters designates the
            format for both formatting and parsing as follows:
            <pre>{`ISO8601TimeZone:
  OneLetterISO8601TimeZone:   Sign TwoDigitHours | Z
  TwoLetterISO8601TimeZone:   Sign TwoDigitHours Minutes | Z
  ThreeLetterISO8601TimeZone: Sign TwoDigitHours : Minutes | Z`}</pre>
            For formatting, if the offset value from GMT is 0, <code>"Z"</code> is produced. If
            the number of pattern letters is 1, any fraction of an hour is ignored. For example,
            if the pattern is <code>"X"</code> and the time zone is <code>"GMT+05:30"</code>,{' '}
            <code>"+05"</code> is produced. For parsing, <code>"Z"</code> is parsed as the UTC
            time zone designator. General time zones are <em>not</em> accepted.
          </li>
        </ul>
      </blockquote>
    </div>
  );
}
